import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import useErrorModal from "../useErrorModal";
import { SetSendMoneyQuote } from "../../actions/sendMoney";
import { postQuote, postQuoteWithAccount } from "../../api/sendMoney";

// types
import type { SendMoneyQuote } from "../../types/sendMoney";
import type { SendMoneyQuoteResponse } from "../../types/api/sendMoney";
import { RootState } from "../../types/redux";

const DEBOUNCE_MS = 1500;

const useSendMoneyQuote = () => {
  const openErrorModal = useErrorModal();
  const dispatch = useDispatch();

  const selectedCountry = useSelector(
    (state: RootState) => state.sendMoney.selectedCountry
  );
  const selectedCurrency = useSelector(
    (state: RootState) => state.sendMoney.selectedCurrency
  );
  const selectedRecipient = useSelector(
    (state: RootState) => state.sendMoney.selectedRecipient
  );

  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState<SendMoneyQuote | null>(null);
  const [walletOperatorDisplay, setWalletOperatorDisplay] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestSeq = useRef(0);   // most recent request issued
  const appliedSeq = useRef(0);  // most recent response applied
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  /** Drop the current quote locally and in the store. */
  const clearQuote = useCallback(() => {
    setQuote(null);
    setWalletOperatorDisplay(null);
    dispatch(SetSendMoneyQuote({ quoteId: null, transactionContextId: null }));
  }, [dispatch]);

  const fetchQuote = useCallback(
    async (sendAmount: number, seq: number) => {
      try {
        setLoading(true);
        setHasError(false);
        let result: SendMoneyQuoteResponse["result"];
        let operatorDisplay: string | null = null;

        if (selectedRecipient) {
          const withAccount = await postQuoteWithAccount(sendAmount, selectedRecipient);
          result = withAccount.result;
          operatorDisplay = withAccount.result.walletOperatorDisplay ?? null;
        } else {
          result = (await postQuote(sendAmount, selectedCountry, selectedCurrency)).result;
        }

        // out-of-order guard: drop this response if a newer one already applied
        if (!mounted.current || seq < appliedSeq.current) return;
        appliedSeq.current = seq;

        setQuote(result.quotes?.[0] ?? null); // single quote for now
        setWalletOperatorDisplay(operatorDisplay);
        dispatch(
          SetSendMoneyQuote({
            quoteId: result.quotes?.[0]?.quoteId ?? null,
            transactionContextId: result.quotes?.[0]?.transactionContextId ?? null,
          })
        );
      } catch (err) {
        if (!mounted.current || seq < appliedSeq.current) return;
        appliedSeq.current = seq;
        setHasError(true);
        clearQuote();
        openErrorModal(err);
      } finally {
        if (mounted.current && seq >= appliedSeq.current) {
          setLoading(false);
        }
      }
    },
    [selectedCountry, selectedCurrency, selectedRecipient, openErrorModal, clearQuote, dispatch]
  );

  const requestQuote = useCallback(
    (sendAmount: number | null) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      // empty/invalid amount cancels any pending call and clears the quote
      if (sendAmount === null || Number.isNaN(sendAmount) || sendAmount <= 0) {
        appliedSeq.current = latestSeq.current + 1;
        setLoading(false);
        setHasError(false);
        clearQuote();
        return;
      }

      // a new valid amount invalidates the previous quote until the new one lands
      appliedSeq.current = latestSeq.current + 1;
      clearQuote();

      debounceTimer.current = setTimeout(() => {
        const seq = ++latestSeq.current;
        fetchQuote(sendAmount, seq);
      }, DEBOUNCE_MS);
    },
    [fetchQuote, clearQuote]
  );

  return {
    loading,
    quote,
    walletOperatorDisplay,
    hasError,
    requestQuote,
  };
};

export default useSendMoneyQuote;